import React, { useEffect, useMemo, useRef, useState } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import { debounce } from "utilities/common";
import { fetchProductDetailsAPI, fetchProductsAPI } from "../../redux/apis/products";

export default function ProductSelect({
  valueProductId,
  onSelect,
  label = "Select Product",
  error,
  helperText,
  filters,
  excludeProductIds = [],
  disabled = false,
  limit = 1000,
  status = "active",
  maxAutoLoadTotal = 200,
  maxConcurrency = 3,
}) {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [value, setValue] = useState(null);

  const excludeSet = useMemo(() => new Set(excludeProductIds.filter(Boolean)), [excludeProductIds]);
  const activeReqIdRef = useRef(0);

  const baseFilters = useMemo(() => {
    return {
      skip: 0,
      limit,
      userType: "admin",
      status,
      ...(filters || {}),
    };
  }, [filters, limit, status]);

  const uniqByProductId = (arr) => {
    const seen = new Set();
    const out = [];
    for (const p of arr || []) {
      const id = p?.productId;
      if (!id || seen.has(id)) continue;
      seen.add(id);
      out.push(p);
    }
    return out;
  };

  const applyExclusions = (arr) => (arr || []).filter((p) => !excludeSet.has(p?.productId));

  const loadOptions = async (searchTerm) => {
    const reqId = ++activeReqIdRef.current;
    setLoading(true);
    try {
      const st = searchTerm || "";
      const first = await fetchProductsAPI({ ...baseFilters, skip: 0, searchTerm: st });
      if (reqId !== activeReqIdRef.current) return;
      const firstRaw = first?.filteredProducts || [];
      const total = Number(first?.total || 0);
      const firstPage = uniqByProductId(applyExclusions(firstRaw));
      setOptions(firstPage);

      // Avoid hammering backend: only auto-load all pages when total is small.
      if (!total || total <= firstPage.length || total > maxAutoLoadTotal) return;

      const totalPages = Math.ceil(total / limit);
      const pages = [];
      for (let page = 1; page < totalPages; page++) pages.push(page);

      const queue = [...pages];
      const workers = Array.from({ length: Math.max(1, Math.min(maxConcurrency, pages.length)) });

      await Promise.all(
        workers.map(async () => {
          while (queue.length > 0) {
            const page = queue.shift();
            if (page === undefined) return;
            if (reqId !== activeReqIdRef.current) return;
            const res = await fetchProductsAPI({ ...baseFilters, skip: page * limit, searchTerm: st });
            if (reqId !== activeReqIdRef.current) return;
            const nextRaw = res?.filteredProducts || [];
            const nextPage = uniqByProductId(applyExclusions(nextRaw));
            if (nextPage.length === 0) continue;
            setOptions((prev) => uniqByProductId([...(prev || []), ...nextPage]));
          }
        })
      );
    } catch (e) {
      if (reqId === activeReqIdRef.current) setOptions([]);
    } finally {
      if (reqId === activeReqIdRef.current) setLoading(false);
    }
  };

  const debouncedLoadOptions = useMemo(
    () => debounce(loadOptions, 400),
    // debounce is stable enough; we just want to recreate when important knobs change
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [baseFilters, excludeSet, maxAutoLoadTotal, maxConcurrency, limit]
  );

  useEffect(() => {
    if (!open) return;
    debouncedLoadOptions(searchTerm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, open]);

  useEffect(() => {
    let cancelled = false;
    async function hydrateSelected() {
      if (!valueProductId) {
        setValue(null);
        return;
      }
      try {
        const res = await fetchProductDetailsAPI(valueProductId);
        if (cancelled) return;
        // API shape differs across environments
        const product =
          res?.productDetails ||
          res?.product ||
          res?.data?.productDetails ||
          res?.data?.product ||
          res?.data ||
          res;

        const normalized = product?.productId ? product : product?.productDetails;
        setValue(normalized || null);
        if (normalized?.productId) {
          // Ensure selected value always matches an option
          setOptions((prev) => uniqByProductId(applyExclusions([normalized, ...(prev || [])])));
        }
      } catch {
        if (!cancelled) setValue(null);
      }
    }
    hydrateSelected();
    return () => {
      cancelled = true;
    };
  }, [valueProductId]);

  return (
    <Autocomplete
      open={open}
      onOpen={() => {
        setOpen(true);
        loadOptions(searchTerm);
      }}
      onClose={() => setOpen(false)}
      disabled={disabled}
      options={options}
      loading={loading}
      value={value}
      isOptionEqualToValue={(option, val) => option?.productId === val?.productId}
      getOptionLabel={(option) => option?.productName || ""}
      onChange={(_, selectedOption) => {
        setValue(selectedOption || null);
        onSelect?.(selectedOption || null);
      }}
      onInputChange={(_, newInputValue, reason) => {
        // Keep a separate search term for API calls; don't fight MUI's internal input state
        if (reason === "input") setSearchTerm(newInputValue ?? "");
        if (reason === "clear") setSearchTerm("");
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          error={Boolean(error)}
          helperText={helperText}
          label={label}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={18} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}

